import React, { useState, useEffect } from 'react';
import { Form, Card, Row, Col, Accordion } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCog,
  faBolt,
  faClock,
  faDatabase,
  faC
} from '@fortawesome/free-solid-svg-icons';
import { faFileLines } from '@fortawesome/free-regular-svg-icons';
import Button from 'components/base/Button';
import {
  fetchCollections,
  searchKnowledgeBase,
  KnowledgeHubCollection,
  KnowledgeHubSearchResult
} from '../../services/knowledgehub.service';
import HVAToast from 'components/common/HVAToast';

const SearchPlayground: React.FC = () => {
  const [collections, setCollections] = useState<KnowledgeHubCollection[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [maxResults, setMaxResults] = useState(5);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<KnowledgeHubSearchResult[]>([]);
  const [totalFound, setTotalFound] = useState(0);
  const [processingTime, setProcessingTime] = useState<number | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');

  useEffect(() => {
    loadCollections();
  }, []);

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const loadCollections = async () => {
    try {
      const data = await fetchCollections();
      setCollections(data);
      // Select all collections by default
      setSelectedCollections(data.map(c => c.id));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      showToastMessage(
        err.response?.data?.message || 'Error loading collections:',
        'error'
      );
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      showToastMessage('Please enter a search query', 'warning');
      return;
    }

    try {
      setSearching(true);
      setResults([]);
      setTotalFound(0);
      setProcessingTime(null);

      const selectedVectorStoreNames = collections
        .filter(col => selectedCollections.includes(col.id))
        .map(
          col =>
            col.vectorStoreName ||
            col.name.toLowerCase().replace(/[^a-z0-9_]/g, '_')
        );

      const startTime = Date.now();
      const response = await searchKnowledgeBase({
        query,
        collection_ids: selectedVectorStoreNames,
        max_results: maxResults
      });

      const endTime = Date.now();
      setProcessingTime(response.processing_time || endTime - startTime);
      setResults(response.results);
      setTotalFound(response.total_found);

      if (response.results.length === 0) {
        showToastMessage(
          'No results found. Try adjusting your query or search parameters.',
          'info'
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      showToastMessage(err.response?.data?.message || 'Search error', 'error');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const toggleCollection = (collectionId: string) => {
    setSelectedCollections(prev =>
      prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const getRelevanceColor = (relevance: number) => {
    if (relevance >= 0.8) return 'success';
    if (relevance >= 0.6) return 'warning';
    return 'secondary';
  };

  const getRelevanceLabel = (relevance: number) => {
    if (relevance >= 0.8) return 'High';
    if (relevance >= 0.6) return 'Medium';
    return 'Low';
  };

  return (
    <div>
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Search Form */}
      <Card className="mb-4">
        <Card.Header className="bg-body-secondary pt-3 pb-3">
          <h5 className="mb-0">Search Knowledge Base</h5>
        </Card.Header>
        <Card.Body className="px-6 bg-body">
          <Form>
            <Row>
              <Col>
                {/* Collections */}
                <Form.Group className="mb-3">
                  <Form.Label className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon icon={faDatabase} className="me-2" />
                    Collections
                  </Form.Label>
                  <div className="border rounded p-2 px-3 bg-body-emphasis">
                    <div className="mt-0">
                      {collections.map(col => (
                        <Form.Check
                          key={col.id}
                          type="checkbox"
                          id={`collection-${col.id}`}
                          label={
                            <span>
                              {col.name}
                              <Badge
                                bg="secondary"
                                variant="phoenix"
                                className="ms-2"
                                pill
                              >
                                {col.documentsCount || 0} docs
                              </Badge>
                            </span>
                          }
                          checked={selectedCollections.includes(col.id)}
                          onChange={() => toggleCollection(col.id)}
                          className="mb-1"
                        />
                      ))}
                    </div>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                {/* Max Results */}
                <Form.Group className="mb-3">
                  <Form.Label className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon icon={faCog} className="me-2" />
                    Max Results
                  </Form.Label>
                  <Form.Select
                    value={maxResults}
                    onChange={e => setMaxResults(Number(e.target.value))}
                  >
                    <option value={3}>3 results</option>
                    <option value={5}>5 results</option>
                    <option value={10}>10 results</option>
                    <option value={20}>20 results</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Search Query */}
            <Form.Group className="mb-3">
              <Form.Label className="text-body-emphasis fw-semibold">
                <FontAwesomeIcon icon={faBolt} className="me-2" />
                Search Query
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter your search query here..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={searching}
                className="fs-9"
                style={{ resize: 'vertical' }}
              />
              <Form.Text className="text-muted">
                Press Enter to search or use the Search button below
              </Form.Text>
            </Form.Group>

            {/* Search Button - ALINEADO A LA DERECHA */}
            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSearch}
                disabled={searching || !query.trim()}
                className="px-4"
              >
                {searching ? (
                  <>
                    <FontAwesomeIcon icon={faC} className="me-1" spin />
                    Searching Collections...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Search Collections
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Search Results */}
      {results.length > 0 && (
        <Card>
          <Card.Header className="bg-body-secondary pt-3 pb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="fw-bold pe-2">Search Results</span>
                {processingTime !== null && (
                  <span className="text-body-tertiary small">
                    {' ( '}
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {processingTime}
                    {'ms )'}
                  </span>
                )}
                {totalFound > 0 && (
                  <Badge bg="primary" variant="phoenix" className="mx-2">
                    {totalFound} results found
                  </Badge>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body className="px-6 bg-body">
            <Accordion>
              {results.map((result, index) => (
                <Accordion.Item key={index} eventKey={String(index)}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={faFileLines}
                          className="me-2 text-body-tertiary fs-8"
                        />
                        <span className="fw-medium text-body-emphasis fs-9">
                          {result.source ||
                            result.document_name ||
                            `Result ${index + 1}`}
                        </span>
                        {result.section && (
                          <Badge
                            bg="secondary"
                            variant="phoenix"
                            text="dark"
                            className="ms-2"
                          >
                            {result.section}
                          </Badge>
                        )}
                      </div>
                      <Badge bg={getRelevanceColor(result.relevance)} pill>
                        {getRelevanceLabel(result.relevance)} (
                        {Math.round(result.relevance * 100)}%)
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="p-3 bg-body-emphasis rounded fs-9">
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {result.text}
                      </p>
                    </div>
                    <div className="mt-3 text-body-tertiary small">
                      <strong>Source:</strong>{' '}
                      {result.source || result.document_name || 'Unknown'}
                      <br />
                      <strong>Section:</strong>{' '}
                      {result.section || result.page_or_section || 'N/A'}
                      <br />
                      <strong>Relevance Score:</strong>{' '}
                      {(result.relevance * 100).toFixed(2)}%
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default SearchPlayground;
